import {AfterViewInit, Component, computed, effect, inject, Injector, input, OnInit, Signal} from '@angular/core';
import {CampaignUserStatus, Destroyable, Location, LoyaltyCurrencyPipe} from 'shared-lib';
import {watchState} from '@ngrx/signals';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime, delay, Subject} from 'rxjs';
import {tap} from 'rxjs/operators';
import CollisionBehavior = google.maps.CollisionBehavior;
import {UserLocationService, UserLocation, Campaign} from 'shared-lib';
import {MyPlacesMapStore} from '../../store/my-places-map.store';

export type CampaignLocationTuple = { campaign: Campaign, location: Location };

@Component({
  template: '',
  standalone: false
})
export abstract class MapComponent extends Destroyable implements OnInit, AfterViewInit {

  filterStore: any = input<any>();

  // emits whenever the map becomes idle (finished moving)
  readonly idle$ = new Subject<void>();


  campaignUserStatus: CampaignUserStatus;
  mapOptions = {
    disableDefaultUI: true,
    mapTypeId: google.maps.MapTypeId.ROADMAP,
    clickableIcons: false,
    gestureHandling: 'greedy'
  } as google.maps.MapOptions;
  center: Signal<google.maps.LatLngLiteral>;
  markerLocationMap: Map<google.maps.marker.AdvancedMarkerElement, Location> = new Map<google.maps.marker.AdvancedMarkerElement, Location>();
  carrouselSectionLocationsCampaignMap: Map<Location, Campaign> = new Map<Location, Campaign>();
  selectedMarker?: google.maps.marker.AdvancedMarkerElement;
  selectedMarkerHtmlElement?: any;
  selectedLocation: Location | undefined;
  googleMap!: google.maps.Map;
  displayedMarkersМаp: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map<string, google.maps.marker.AdvancedMarkerElement>();
  isZoomChanged = false;
  protected abstract getStore: () => MyPlacesMapStore;
  store = computed<MyPlacesMapStore>(() => this.getStore());
  private isAppAction = false;
  private readonly MAP_ZOOM_KNOWN_LOCATION = 12;
  zoom = this.MAP_ZOOM_KNOWN_LOCATION;
  private readonly MAP_ZOOM_UNKNOWN_LOCATION = 4;
  readonly #injector = inject(Injector);
  private geoBoundsSubject = new Subject();
  private geoBoundsObservable = this.geoBoundsSubject.asObservable().pipe(debounceTime(200),
    tap(_ => {
      const bounds = this.googleMap.getBounds();
      if (bounds) {
        const geoBounds = {
          southWest: {latitude: bounds.getSouthWest().lat(), longitude: bounds.getSouthWest().lng()},
          northEast: {latitude: bounds.getNorthEast().lat(), longitude: bounds.getNorthEast().lng()}
        };
        this.filterStore().updateValue({...this.filterStore().filter(), geoBounds: geoBounds});
      }
    }));

  private carrouselDisplayedItemsSubject = new Subject()
  private carrouselDisplayedItems$ = this.carrouselDisplayedItemsSubject.asObservable().pipe(delay(1000),
    tap(() => {
      const newLocationsCampaignMap = new Map<Location, Campaign>();
      const idToLocationMap = new Map<string, CampaignLocationTuple>();
      this.carrouselSectionLocationsCampaignMap.forEach((campaign, location) => {
        if (location !== undefined) {
          idToLocationMap.set(location.id, {campaign: campaign, location: location} as CampaignLocationTuple);
        }
      });
      this.displayedMarkersМаp.forEach((marker, id) => {
        const el = marker.element; // HTMLElement (the <gmp-advanced-marker>)
        const isVisible = el && (el.slot as string).indexOf('visible') >= 0; // visible in layout
        if (isVisible) {
          const tuple: CampaignLocationTuple = idToLocationMap.get(id)!;
          if (tuple) {
            newLocationsCampaignMap.set(tuple.location, tuple.campaign);
          }
        }
      });
      this.carrouselSectionLocationsCampaignMap = newLocationsCampaignMap;
    }));

  protected constructor(private readonly currencyPipe: LoyaltyCurrencyPipe, userLocationService: UserLocationService, campaignUserStatus: CampaignUserStatus) {
    super();
    this.campaignUserStatus = campaignUserStatus;
    const userLocation = toSignal<UserLocation | undefined>(userLocationService.userLocation$);
    this.center = computed(() => {
      if (userLocation()) {
        this.zoom = this.MAP_ZOOM_KNOWN_LOCATION;
        return {lat: userLocation()?.latitude!, lng: userLocation()?.longitude!}
      } else {
        this.zoom = this.MAP_ZOOM_UNKNOWN_LOCATION;
        return {lat: userLocationService.getUserLocation().latitude, lng: userLocationService.getUserLocation().longitude};
      }
    });

    effect(() => {
      const newLocationIds: string[] = [];
      const newLocationsCampaignMap: Map<Location, Campaign> = new Map<Location, Campaign>();
      this.store().campaigns().forEach(campaign => {
        return campaign.locationsGroup?.locations?.map(item => {
          newLocationIds.push(item.id);
          newLocationsCampaignMap.set(item, campaign);
          if (this.displayedMarkersМаp.has(item.id)) {
            return;
          }
          const markerOptions = {
            id: campaign.locationsGroup!.locations![0].id,
            collisionBehavior: CollisionBehavior.OPTIONAL_AND_HIDES_LOWER_PRIORITY,
            position: {
              lat: item.address.latitude,
              lng: item.address.longitude,
            } as google.maps.LatLngLiteral,
            content: this.buildContent(campaign.logoUrlMobileOptimized!, campaign.reward, item.id),
            gmpClickable: true
          } as google.maps.marker.AdvancedMarkerElementOptions;
          const marker = new google.maps.marker.AdvancedMarkerElement(markerOptions)
          marker.addListener("click", ($event: any) => this.onMapMarkerClick($event, marker));
          marker.id = item.id;
          marker.setAttribute("campaignId", campaign.id);
          this.markerLocationMap.set(marker, item);
          //Add markers that are not in the current display view site
          marker.map = this.googleMap;
          this.displayedMarkersМаp.set(marker.id, marker);
        });
      });

      if (this.isZoomChanged) {
        //Hide markers that are not in the current display view site
        this.displayedMarkersМаp.forEach((marker, id) => {
          if (newLocationIds.indexOf(id) < 0) {
            this.disposeAdvancedMarker(marker);
            this.markerLocationMap.delete(marker)
            this.displayedMarkersМаp.delete(id);
          }
        });
        this.carrouselSectionLocationsCampaignMap = newLocationsCampaignMap;
      } else {
        newLocationsCampaignMap.forEach((campaign, location) => {
          this.carrouselSectionLocationsCampaignMap.set(location, campaign)
        });
      }

      //Google maps collision resolution hides some of the markers. Fow now this seems to produce a better UX result than forcing all
      //markers to be visible. This is a workaround for this behaviour. We can see what are the visible markers after they are drawn.
      //This timeout is chosen by trail and error. After markers are displayed, hidden are removed from the list and passed to carrousel section.
      this.carrouselDisplayedItemsSubject.next({});
    });
  }

  ngAfterViewInit(): void {
    this.idle$
      .pipe(
        debounceTime(200),                           // collapse bursts
        tap(_ => {
          const currentZoom = this.googleMap.getZoom();
          if (currentZoom != this.zoom) {
            this.zoom = currentZoom!;
            this.isZoomChanged = true;
          } else {
            this.isZoomChanged = false;
          }

          if (this.isAppAction) {
            this.isAppAction = false;
          } else {
            const bounds = this.googleMap.getBounds();
            if (bounds) {
              const geoBounds = {
                southWest: {latitude: bounds.getSouthWest().lat(), longitude: bounds.getSouthWest().lng()},
                northEast: {latitude: bounds.getNorthEast().lat(), longitude: bounds.getNorthEast().lng()}
              };
              this.filterStore().updateValue({...this.filterStore().filter(), geoBounds: geoBounds});
            }
          }
        })
      )
      .subscribe();

    this.carrouselDisplayedItems$.subscribe();
  }

  ngOnInit(): void {
    watchState(this.store(), () => {
      this.clearSelection();
    }, {injector: this.#injector});
    this.geoBoundsObservable.subscribe();
  }

  onMapMarkerClick(event: any, marker: any): void {
    if (!event) {
      return;
    }
    if (marker) {
      this.selectedLocation = this.markerLocationMap.get(marker);
    }
    this.selectedMarker = marker;
    this.selectedMarkerHtmlElement?.classList.remove('selected');
    this.selectedMarkerHtmlElement = event.domEvent.target;
    this.selectedMarkerHtmlElement.classList.add('selected');
  }

  onMapClick(event: any): void {
    this.clearSelection();
  }

  onSelectedCampaignChange($event: Location): void {
    this.selectedMarkerHtmlElement?.classList.remove('selected');
    this.selectedLocation = $event;
    this.selectedMarker = this.displayedMarkersМаp.get($event.id)!;

    this.selectedMarkerHtmlElement = (this.selectedMarker.content! as any).children[0];
    this.selectedMarkerHtmlElement.classList.add('selected');

    //Centers the map to the selected marker.
    this.isAppAction = true;
    setTimeout(() => this.panToWithOffset({lat: $event.address.latitude, lng: $event.address.longitude}), 100);
  }

  clearSelection() {
    this.selectedMarkerHtmlElement?.classList.remove('selected');
    this.selectedMarker = undefined;
    this.selectedLocation = undefined;
    this.selectedMarkerHtmlElement = undefined;
  }

  onMapReady($event: google.maps.Map) {
    this.googleMap = $event;
  }

  private disposeAdvancedMarker(marker: google.maps.marker.AdvancedMarkerElement): void {
    setTimeout(() => {
      // 1) remove from map (official)
      marker.map = null;

      // 2) detach your custom content (break strong DOM refs)
      try {
        // if you provided a custom content node, clear it so GC can reclaim it
        (marker as any).content = null; // TS may need `as any`
      } catch {
      }

      // 3) remove the web component wrapper if present (in v=beta channel)
      const el = (marker as any).element as HTMLElement | undefined;
      if (el && el.tagName?.toLowerCase() === 'gmp-advanced-marker') {
        el.remove(); // physically detach from DOM
      }
    }, 50);
  }

  private panToWithOffset(latLng: | google.maps.LatLng | google.maps.LatLngLiteral | google.maps.LatLngAltitudeLiteral): void {
    //Centers the map to the selected coordinates.
    this.googleMap.panTo(latLng);

    // //Moves the center with predefined offset to prevent the marker to be placed behind the cards at the bottom.
    // //The offset number is selected by empirical testing.
    this.googleMap.panBy(0, 100);
    //Google maps seems to have a strange behaviour when changing the focused marker change makes a large jump on the map.
    //Occasionally the markers are disappearing and until map interaction. This zoom simulates map interaction that causes
    //markers to be always visible.
    setTimeout(() => this.googleMap.setZoom(this.googleMap.getZoom()! - 0.01), 100);
  }


  private buildContent(logo: string, reward: number, locationId: string): any {
    const formattedReward = this.currencyPipe.transform(reward);
    const content = document.createElement("div");
    content.classList.add("map-icon-container", "free-money-map-icon-container");
    content.innerHTML = `
        <img id="${locationId}" src="${logo}" alt="logo" priority>
        <div class="reward-chip">${formattedReward}</div>
    `;
    return content;
  }

}
